import React, { useState, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { incidentsApi } from '../../../../functions/IncidentsApi';
import { debounce } from 'lodash';
import OutlinedInput from '@mui/material/OutlinedInput';
import useIsVisible from '../../../../hooks/useIsVisible';
import { filterSelectedAutocompleteOptions } from '../../../ApiAutocomplete/ApiAutocompleteByEndpoint';

const ApiAutocomplete = ({
  id,
  value,
  onChange,
  enumClass,
  multiple = false,
  disabled = false,
  size = 'small',
  placeholder = 'Start typing to search...',
  sx = {},
  extraParams,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const { isVisible, bindingRefCallback, currentComponentRef } = useIsVisible({
    finishWhenVisible: true,
  });

  const fetchOptions = useCallback(
    debounce(async (searchValue = '') => {
      // fetch only when a user sees this selector
      if (!enumClass || !isVisible) {
        return;
      }

      setLoading(true);
      try {
        const response = await incidentsApi.getAutocompleteOptions(
          enumClass,
          searchValue,
          extraParams
        );

        if (response.status === 200) {
          const newOptions = response.data.data || [];
          setOptions(newOptions);

          // Clear the selector if something in the form that this selector depends on
          // was changed and the new options don't match
          // skip if newOptions it`s search
          if (
            currentComponentRef.current?.classList &&
            !currentComponentRef.current.classList.contains('Mui-focused')
          ) {
            filterSelectedAutocompleteOptions({
              value,
              options: newOptions,
              multiple,
              onChange,
            });
          }
        } else throw response;
      } catch (error) {
        console.error('Error fetching options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [enumClass, isVisible, extraParams]
  );

  // init selector options
  useEffect(() => {
    fetchOptions(value ?? '');
  }, [fetchOptions]);

  return (
    <Autocomplete
      ref={bindingRefCallback}
      id={id}
      multiple={multiple}
      options={options}
      value={multiple ? value || [] : value || null}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue, reason) => {
        // only users change allow
        if (!event) {
          return;
        }

        // allow only input changes for fetching
        // or clear
        const isInput =
          event.target &&
          event.target.nodeType === 1 &&
          typeof event.target.tagName === 'string' &&
          event.target.tagName.toLowerCase() === 'input';

        if (!isInput && reason !== 'clear') {
          return;
        }

        fetchOptions(newInputValue);
      }}
      loading={loading}
      disabled={disabled}
      size={size}
      sx={sx}
      getOptionLabel={(option) => {
        if (typeof option === 'number') {
          return option.toString();
        }

        return option ? option.replace(/_/g, ' ') : '';
      }}
      isOptionEqualToValue={(option, value) => {
        return option === value;
      }}
      renderInput={(params) => {
        return (
          <OutlinedInput
            {...params.InputProps}
            inputProps={params.inputProps}
            placeholder={placeholder}
            size={size}
            fullWidth
            variant="outlined"
            endAdornment={
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            }
          />
        );
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ padding: '8px 16px !important' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {option.replace(/_/g, ' ')}
          </Typography>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={option}
            label={option.replace(/_/g, ' ')}
            size="small"
            {...getTagProps({ index })}
          />
        ))
      }
    />
  );
};

export default ApiAutocomplete;
